import type { HazardKind } from './hazards';

export type MapId = 'sky' | 'dunes';

/** sRGB hex colours (e.g. "#aabbcc"). Renderers convert to linear. */
export type Hex = string;

/**
 * World conventions (shared by sim and renderer): x = east, y = up, z = south.
 * North (−z) is the goal direction: score is northward progress and the storm advances north.
 */

export interface HazardSpawn {
  kind: HazardKind;
  weight: number;
  /** Only spawn beyond this northward progress (m). */
  minDistance: number;
  /** Uniform scale range. */
  scale: [number, number];
  /** Weight multiplier gained per 1000 m (lets nastier hazards ramp in). */
  weightGrowth?: number;
}

/** Non-solid scenery: you roll straight through (bushes rustle). */
export type DecorKind = 'bush' | 'dryBush' | 'grassTuft' | 'waterfall' | 'skyIsland';

export interface DecorSpawn {
  kind: DecorKind;
  /** Expected count per 10 000 m² of ground (waterfalls: per island). */
  density: number;
  scale: [number, number];
  /** Islands: keep at least this far inside the rim (m). */
  inset?: number;
  /** Only place where the ground is this flat (|∇h|). */
  maxSlope?: number;
  /** Clustered placement: items per cluster. */
  cluster?: [number, number];
  /** Floating background islands: vertical offset bands (kept well clear of playable heights). */
  lift?: [number, number][];
}

/** Solid scenery (groves, houses, mesas): placed in clusters, collides like any hazard. */
export interface LandmarkSpawn {
  kind: HazardKind;
  density: number;
  scale: [number, number];
  inset?: number;
  maxSlope?: number;
  cluster?: [number, number];
  /** Only beyond this progress (m). */
  minDistance?: number;
}

export interface SwellDef {
  /** Two swell layers (crest spacing in metres) blended by a noise mask. */
  wavelengthA: number;
  wavelengthB: number;
  /** Crest height range at the start and at full difficulty. */
  amplitude: [number, number];
  amplitudeLate: [number, number];
  /** Where the crest sits in each period (0..1). >0.5 = long gentle rise heading north, steep drop. */
  crest: number;
  /** How far (m) crest lines meander. */
  warp: number;
  /** Small cross-swells and very large undulations. */
  crossAmplitude: number;
  macroAmplitude: number;
}

export interface IslandsDef {
  enabled: boolean;
  /** Distance between the lanes of neighbouring island chains (m). */
  chainSpacing: number;
  /** How far a chain may wander from its lane (m). */
  meander: number;
  /** Max sideways drift per hop (tangent of the heading). */
  turn: number;
  /** Gap between consecutive islands at the start and at full difficulty (m). */
  gap: [number, number];
  gapLate: [number, number];
  radius: [number, number];
  radiusLate: [number, number];
  /** Islands are stretched along the line to the next island by this factor (runway length). */
  elongation: [number, number];
  /** Top surface base height range. */
  top: [number, number];
  /** Underside depth as a multiple of radius. */
  depth: [number, number];
  /** Height of the raised rim that launches you toward the next island. */
  lip: number;
  /** Rolling hills on islands, as a fraction of the swell amplitude. */
  hillScale: number;
}

export interface TerrainDef {
  style: 'meadow' | 'dune';
  swell: SwellDef;
  islands: IslandsDef;
  /** Below this height with no ground beneath you, you've fallen. */
  fallY: number;
  /** Progress (m) at which difficulty peaks. */
  difficultyDistance: number;
  /** Gentle, obstacle-free area around the start (m). */
  startRadius: number;
}

export interface GateDef {
  /** Chance per gate cell of a wind gate over a good ramp. */
  chance: number;
  cell: number;
  radius: number;
  /** Speed multiplier for threading a gate. */
  boost: number;
  /** Metres the storm is pushed back. */
  stormPush: number;
}

export interface PaletteDef {
  skyTop: Hex;
  skyHorizon: Hex;
  skyBottom: Hex;
  sun: Hex;
  sunGlow: Hex;
  ambientSky: Hex;
  ambientGround: Hex;
  shadow: Hex;
  fog: Hex;
  groundA: Hex;
  groundB: Hex;
  groundC: Hex;
  groundDry: Hex;
  cliff: Hex;
  cliffDark: Hex;
  rock: Hex;
  rockDark: Hex;
  moss: Hex;
  bark: Hex;
  leafA: Hex;
  leafB: Hex;
  leafC: Hex;
  accent: Hex;
  water: Hex;
}

export interface LightingDef {
  /** Sun elevation & azimuth (degrees). Azimuth 0 = sun in the north, positive turns east. */
  sunElevation: number;
  sunAzimuth: number;
  sunIntensity: number;
  ambientIntensity: number;
  /** Diffuse wrap (0 = Lambert, 1 = fully wrapped). */
  wrap: number;
  exposure: number;
  saturation: number;
  contrast: number;
  gradeShadows: Hex;
  gradeHighlights: Hex;
}

/** Atmosphere: what makes a map feel deep, hazy and lit by a low sun. */
export interface AtmosphereDef {
  /** Screen-space sun shafts (god rays) strength. */
  shafts: number;
  /**
   * The far landmark on the northern horizon — the place you are always running toward and
   * never reach. Rendered as a hazy silhouette with a beam of light rising from its summit.
   */
  /**
   * The far-north landmark — `beam`: a thin beam of light rising from the ground on the horizon,
   * as if someone out there were signalling you; `peak`: an inverted mountain hanging over the
   * horizon, light falling from its tip; `door`: a lone doorway with light pouring through.
   * Stone colour, light colour, overall scale (m), haze.
   */
  beacon: { kind: 'beam' | 'peak' | 'door'; color: Hex; beam: Hex; height: number; haze: number };
  /** Soft cloud shadows drifting across the land (0 = none, 1 = full shade). */
  cloudShadows: number;
  /** Patchy, drifting ground mist / blowing haze: density, base height, thickness (m). */
  mist: { density: number; base: number; height: number };
  /** Layered silhouettes on the horizon: distant cloud banks or far dune ridges. */
  horizon: { kind: 'clouds' | 'dunes'; color: Hex; shade: Hex; height: number };
}

export interface FogDef {
  density: number;
  heightFalloff: number;
  baseHeight: number;
  skyBlend: number;
  sunScatter: number;
}

export interface CloudDef {
  enabled: boolean;
  bottom: number;
  top: number;
  coverage: number;
  density: number;
  scale: number;
  detailScale: number;
  windSpeed: number;
  ambientTop: Hex;
  ambientBottom: Hex;
  /** Painted cloud band in the sky dome. */
  skyBand: number;
}

export interface GrassDef {
  enabled: boolean;
  /** Blades per m² in the nearest ring at high quality. */
  density: number;
  height: [number, number];
  width: number;
  base: Hex;
  tip: Hex;
  tipDry: Hex;
  flowers: Hex[];
  /** Fraction of blades that are flowers (in flower patches). */
  flowerRatio: number;
}

export interface SandDef {
  enabled: boolean;
  lit: Hex;
  shade: Hex;
  deep: Hex;
  sparkle: Hex;
  /** Ripple normal-map tiling (m per tile). */
  rippleScale: number;
  sparkleDensity: number;
}

export interface WindDef {
  /** Wind heading in degrees (0 = blowing toward north, positive turns east). */
  heading: number;
  strength: number;
  gustiness: number;
  particles: 'pollen' | 'sand';
  particleCount: number;
}

export interface ChaserDef {
  name: string;
  verb: string;
  startGap: number;
  baseSpeed: number;
  accel: number;
  maxSpeed: number;
  maxLeadStart: number;
  maxLeadEnd: number;
  maxLeadRampTime: number;
  color: Hex;
  glow: Hex;
}

/** Overlay colours: ink on paper, nothing louder. */
export interface UiTheme {
  ink: Hex;
  inkSoft: Hex;
  paper: Hex;
  glow: Hex;
}

export interface MapDef {
  id: MapId;
  name: string;
  terrain: TerrainDef;
  hazards: HazardSpawn[];
  /** Obstacles per 10 000 m² at the start and at full difficulty. */
  hazardDensity: [number, number];
  gates: GateDef;
  landmarks: LandmarkSpawn[];
  decor: DecorSpawn[];
  palette: PaletteDef;
  lighting: LightingDef;
  fog: FogDef;
  atmosphere: AtmosphereDef;
  clouds: CloudDef;
  grass: GrassDef;
  sand: SandDef;
  wind: WindDef;
  chaser: ChaserDef;
  ui: UiTheme;
  audioTheme: MapId;
  fallVerb: string;
}

export const SKY_ISLES: MapDef = {
  id: 'sky',
  name: 'Sky Isles',
  terrain: {
    style: 'meadow',
    swell: {
      wavelengthA: 74,
      wavelengthB: 52,
      amplitude: [3.5, 6.5],
      amplitudeLate: [4.5, 8.5],
      crest: 0.6,
      warp: 26,
      crossAmplitude: 1.2,
      macroAmplitude: 0,
    },
    islands: {
      enabled: true,
      chainSpacing: 200,
      meander: 90,
      turn: 0.5,
      gap: [7, 15],
      gapLate: [12, 24],
      radius: [42, 60],
      radiusLate: [36, 54],
      elongation: [1.3, 1.65],
      top: [-8, 10],
      depth: [1.1, 1.8],
      lip: 3.4,
      hillScale: 1,
    },
    fallY: -24,
    difficultyDistance: 4000,
    startRadius: 70,
  },
  hazards: [
    { kind: 'boulder', weight: 5, minDistance: 0, scale: [1.3, 2.8] },
    { kind: 'mossBoulder', weight: 4, minDistance: 0, scale: [1.3, 2.6] },
    { kind: 'tree', weight: 5, minDistance: 0, scale: [1.05, 1.45] },
    { kind: 'pine', weight: 3, minDistance: 250, scale: [1.05, 1.45] },
    { kind: 'pillar', weight: 2, minDistance: 400, scale: [1.2, 1.6], weightGrowth: 0.3 },
    { kind: 'gate', weight: 1, minDistance: 600, scale: [1.1, 1.25], weightGrowth: 0.2 },
  ],
  hazardDensity: [2.2, 4.6],
  gates: { chance: 0.62, cell: 112, radius: 5.5, boost: 0.12, stormPush: 45 },
  landmarks: [
    { kind: 'tree', density: 5, scale: [1.0, 1.6], inset: 6, cluster: [3, 7] },
    { kind: 'pine', density: 2.2, scale: [1.0, 1.5], inset: 6, cluster: [2, 5] },
    { kind: 'boulder', density: 1.6, scale: [1.2, 2.8], inset: 4, cluster: [1, 3] },
    { kind: 'mossBoulder', density: 1.6, scale: [1.2, 2.6], inset: 4, cluster: [1, 3] },
    { kind: 'house', density: 0.45, scale: [0.95, 1.2], inset: 10, maxSlope: 0.16, cluster: [1, 3] },
    { kind: 'ruin', density: 0.35, scale: [0.9, 1.3], inset: 8 },
  ],
  decor: [
    { kind: 'bush', density: 16, scale: [0.8, 1.5], inset: 3, cluster: [2, 6] },
    { kind: 'waterfall', density: 1.5, scale: [0.9, 1.4] },
  ],
  palette: {
    skyTop: '#3d7fd6',
    skyHorizon: '#c4e3f6',
    skyBottom: '#e9f4fb',
    sun: '#ffe4bd',
    sunGlow: '#ffd29a',
    ambientSky: '#a9cdea',
    ambientGround: '#7b8e5a',
    shadow: '#6a7cb0',
    fog: '#d9e6ee',
    groundA: '#3c6b2a',
    groundB: '#6a9436',
    groundC: '#a8b955',
    groundDry: '#b9a865',
    cliff: '#a39486',
    cliffDark: '#706578',
    rock: '#76727d',
    rockDark: '#45465a',
    moss: '#4c6f27',
    bark: '#6e5242',
    leafA: '#163a30',
    leafB: '#4a8530',
    leafC: '#a9c64e',
    accent: '#d9472b',
    water: '#bfe6ff',
  },
  lighting: {
    // Golden hour, low in the north-west: you run *into* the light — backlit grass glows,
    // shadows stretch toward you and sun shafts pour over the islands ahead.
    sunElevation: 22,
    sunAzimuth: -32,
    sunIntensity: 2.4,
    ambientIntensity: 1.95,
    wrap: 0.5,
    exposure: 0.95,
    saturation: 1.0,
    contrast: 0.95,
    gradeShadows: '#6a78b8',
    gradeHighlights: '#fff0d6',
  },
  fog: { density: 0.0017, heightFalloff: 0.006, baseHeight: -40, skyBlend: 0.8, sunScatter: 0.9 },
  atmosphere: {
    shafts: 0.55,
    beacon: { kind: 'beam', color: '#8ea0b8', beam: '#fff1d2', height: 820, haze: 0.62 },
    cloudShadows: 0.42,
    mist: { density: 0.35, base: -4, height: 9 },
    horizon: { kind: 'clouds', color: '#fff4e6', shade: '#a9b6d0', height: 260 },
  },
  clouds: {
    enabled: true,
    bottom: -230,
    top: -78,
    coverage: 0.56,
    density: 0.03,
    scale: 760,
    detailScale: 120,
    windSpeed: 6,
    ambientTop: '#e6f1fb',
    ambientBottom: '#7f93bb',
    skyBand: 0.55,
  },
  grass: {
    enabled: true,
    density: 62,
    height: [0.24, 0.52],
    width: 0.024,
    base: '#22482a',
    tip: '#7f9c45',
    tipDry: '#b8a85e',
    // Daisy, buttercup, poppy, lupine, clover.
    flowers: ['#fffaf2', '#ffd23a', '#f2553c', '#9a7ae0', '#e58fc0'],
    flowerRatio: 0.035,
  },
  sand: {
    enabled: false,
    lit: '#f0c98a',
    shade: '#c58a63',
    deep: '#8e5a54',
    sparkle: '#fff6e0',
    rippleScale: 6,
    sparkleDensity: 0,
  },
  wind: { heading: 60, strength: 1, gustiness: 0.8, particles: 'pollen', particleCount: 500 },
  chaser: {
    name: 'the Squall',
    verb: 'swallowed by the squall',
    startGap: 160,
    baseSpeed: 16,
    accel: 0.1,
    maxSpeed: 46,
    maxLeadStart: 260,
    maxLeadEnd: 150,
    maxLeadRampTime: 160,
    color: '#2e3557',
    glow: '#9fb6ff',
  },
  ui: { ink: '#1d2b4f', inkSoft: '#4f6185', paper: '#f6f8fb', glow: '#ffffff' },
  audioTheme: 'sky',
  fallVerb: 'lost to the clouds',
};

export const SUNSCAR_DUNES: MapDef = {
  id: 'dunes',
  name: 'Sunscar Dunes',
  terrain: {
    style: 'dune',
    swell: {
      wavelengthA: 96,
      wavelengthB: 64,
      amplitude: [6, 11],
      amplitudeLate: [8, 15],
      crest: 0.64,
      warp: 40,
      crossAmplitude: 2.2,
      macroAmplitude: 12,
    },
    islands: { enabled: false, chainSpacing: 1, meander: 0, turn: 0, gap: [0, 0], gapLate: [0, 0], radius: [0, 0], radiusLate: [0, 0], elongation: [1, 1], top: [0, 0], depth: [0, 0], lip: 0, hillScale: 0 },
    fallY: -1e9,
    difficultyDistance: 4000,
    startRadius: 70,
  },
  hazards: [
    { kind: 'sandstone', weight: 6, minDistance: 0, scale: [1.4, 3.0] },
    { kind: 'cactus', weight: 4, minDistance: 0, scale: [1.1, 1.5] },
    { kind: 'deadTree', weight: 2, minDistance: 200, scale: [1.1, 1.5] },
    { kind: 'spire', weight: 3, minDistance: 300, scale: [1.0, 1.4], weightGrowth: 0.3 },
    { kind: 'arch', weight: 1.4, minDistance: 500, scale: [0.95, 1.15], weightGrowth: 0.3 },
  ],
  hazardDensity: [2.0, 4.4],
  gates: { chance: 0.5, cell: 120, radius: 5.5, boost: 0.12, stormPush: 45 },
  landmarks: [
    { kind: 'sandstone', density: 1.8, scale: [1.6, 4.2], cluster: [1, 4] },
    { kind: 'cactus', density: 1.4, scale: [1.1, 1.6], cluster: [1, 3] },
    { kind: 'deadTree', density: 0.6, scale: [1.1, 1.6] },
    { kind: 'spire', density: 0.3, scale: [1.4, 3.0], cluster: [1, 3] },
    { kind: 'arch', density: 0.1, scale: [1.4, 2.4], minDistance: 150 },
    { kind: 'mesa', density: 0.07, scale: [1.0, 2.4], minDistance: 150 },
  ],
  decor: [
    { kind: 'grassTuft', density: 26, scale: [0.7, 1.4], cluster: [3, 8], maxSlope: 0.35 },
    { kind: 'dryBush', density: 7, scale: [0.7, 1.4], cluster: [1, 3] },
  ],
  palette: {
    skyTop: '#6f90c6',
    skyHorizon: '#f6dcbc',
    skyBottom: '#f1cfae',
    sun: '#ffe8c8',
    sunGlow: '#ffc488',
    ambientSky: '#c4c1e2',
    ambientGround: '#d6a27a',
    shadow: '#b48cb4',
    fog: '#efd6bf',
    groundA: '#e7b878',
    groundB: '#f0c98a',
    groundC: '#d9a066',
    groundDry: '#c98a5c',
    cliff: '#d07c4a',
    cliffDark: '#8a4436',
    rock: '#d9824e',
    rockDark: '#8c3f33',
    moss: '#9aa04a',
    bark: '#8c7a6c',
    leafA: '#4d5530',
    leafB: '#7d7f4c',
    leafC: '#b3a56e',
    accent: '#e5552e',
    water: '#9fd8e8',
  },
  lighting: {
    sunElevation: 16,
    sunAzimuth: -58,
    sunIntensity: 2.5,
    ambientIntensity: 1.75,
    wrap: 0.45,
    exposure: 0.93,
    saturation: 0.97,
    contrast: 0.95,
    gradeShadows: '#7a7fae',
    gradeHighlights: '#fff0d6',
  },
  fog: { density: 0.0018, heightFalloff: 0.011, baseHeight: -12, skyBlend: 0.75, sunScatter: 1.0 },
  atmosphere: {
    shafts: 0.6,
    beacon: { kind: 'beam', color: '#9a8478', beam: '#fff2d8', height: 900, haze: 0.58 },
    cloudShadows: 0.22,
    mist: { density: 0.45, base: -10, height: 12 },
    horizon: { kind: 'dunes', color: '#e9c2a8', shade: '#b48e98', height: 120 },
  },
  clouds: {
    enabled: false,
    bottom: 0,
    top: 0,
    coverage: 0,
    density: 0,
    scale: 1,
    detailScale: 1,
    windSpeed: 0,
    ambientTop: '#ffffff',
    ambientBottom: '#ffffff',
    skyBand: 0.3,
  },
  grass: { enabled: false, density: 0, height: [0.3, 0.6], width: 0.05, base: '#7a6a2e', tip: '#d8c06a', tipDry: '#e8d08a', flowers: [], flowerRatio: 0 },
  sand: {
    enabled: true,
    lit: '#efcca2',
    shade: '#d6a088',
    deep: '#ba8a80',
    sparkle: '#fff7e2',
    rippleScale: 5,
    sparkleDensity: 0.45,
  },
  wind: { heading: -30, strength: 1.2, gustiness: 1, particles: 'sand', particleCount: 3000 },
  chaser: {
    name: 'the Haboob',
    verb: 'buried by the haboob',
    startGap: 160,
    baseSpeed: 16.5,
    accel: 0.105,
    maxSpeed: 47,
    maxLeadStart: 260,
    maxLeadEnd: 150,
    maxLeadRampTime: 160,
    color: '#7a3f2a',
    glow: '#ffc58a',
  },
  ui: { ink: '#4a2219', inkSoft: '#8a5340', paper: '#fff6ea', glow: '#fff3dc' },
  audioTheme: 'dunes',
  fallVerb: 'swallowed by the sand',
};

export const MAPS: Record<MapId, MapDef> = { sky: SKY_ISLES, dunes: SUNSCAR_DUNES };
/** Menu order; the first is the default map. */
export const MAP_ORDER: MapId[] = ['dunes', 'sky'];

/** How many visual variants @cloudpearl/assets builds per decor kind. */
export const DECOR_VARIANTS: Record<DecorKind, number> = {
  bush: 3,
  dryBush: 2,
  grassTuft: 3,
  waterfall: 1,
  skyIsland: 4,
};
